import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { identifyUser, resetUser } from '../analytics/posthog';

// Get the API URL based on the environment
const getApiUrl = () => {
    // In production, use the environment variable or construct from window.location
    const apiUrl = config.API_URL;
    if (apiUrl) return apiUrl;

    // Fallback: Replace 'frontend' with 'backend' in the hostname
    const currentHost = window.location.host;
    if (currentHost.includes('frontend')) {
        return `https://${currentHost.replace('frontend', 'backend')}`;
    }
    
    console.warn('Unable to determine API URL, using relative URLs');
    return '';
};

// Create and export axios instance with base URL
export const api = axios.create({
    baseURL: getApiUrl(),
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    }
});

// Add request interceptor for any future custom headers
api.interceptors.request.use((config) => {
  // Ensure headers object exists
  config.headers = config.headers || {};
  return config;
});

interface User {
    id: number;
    name: string;
    email: string;
    profile_picture?: string;
    credits: number;
    stripe_customer_id?: string;
    created_at?: string;
}

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: User | null;
    api: typeof api;
    login: (email: string, password: string) => Promise<void>;
    register: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState<User | null>(null);

    // Check authentication status on mount and handle Google OAuth callback
    useEffect(() => {        
        if (window.location.pathname === '/login') {
            setIsLoading(false);
            return; // Do not check auth on login page
        }

        const checkAuth = async () => {
            try {
                setIsLoading(true);  // Start loading
                // Check if this is a Google OAuth callback
                const urlParams = new URLSearchParams(window.location.search);
                const code = urlParams.get('code');
                const state = urlParams.get('state');

                if (code && state) {
                    try {
                        // Instead of redirecting, make an API call
                        const response = await api.get(`/api/auth/google/callback?${urlParams.toString()}`);
                        setIsAuthenticated(true);
                        const userData = response.data.user;
                        setUser(userData);
                        // Identify user in PostHog
                        identifyUser(userData.id.toString(), {
                            email: userData.email,
                            name: userData.name,
                            created_at: userData.created_at,
                            auth_method: 'google'
                        });
                        // Redirect to home after successful login
                        window.location.href = '/';
                    } catch (error) {
                        console.error('Google OAuth callback failed:', error);
                        window.location.href = '/login?error=google_auth_failed';
                    }
                    return;
                }

                // Regular auth check
                try {
                    const response = await api.get('/api/auth/me');
                    setIsAuthenticated(true);
                    const userData = response.data.user;
                    setUser(userData);
                    // Identify user in PostHog
                    identifyUser(userData.id.toString(), {
                        email: userData.email,
                        name: userData.name,
                        created_at: userData.created_at
                    });
                } catch (error: any) {
                    console.error('Auth check failed:', error);
                    // Only set not authenticated if we get a 401/403 error
                    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                        setIsAuthenticated(false);
                        setUser(null);
                    }
                }
            } catch (error) {
                console.error('Auth check error:', error);
                setIsAuthenticated(false);
                setUser(null);
            } finally {
                setIsLoading(false);  // End loading regardless of outcome
            }
        };

        // Add a small delay to ensure cookie is set
        setTimeout(checkAuth, 100);
    }, []);

    // Add axios request interceptor to include token
    useEffect(() => {
      const requestInterceptor = api.interceptors.request.use(config => {
        // Cookies are automatically included with withCredentials: true
        return config;
      });

      // Add axios response interceptor to handle token refresh
      const responseInterceptor = api.interceptors.response.use(
        response => response,
        async error => {
          const originalRequest = error.config;
          
          // Only handle authentication errors
          if (error.response?.status === 401 && error.config.url !== '/api/refresh') {
            // If this is already a retry attempt, clear auth and reject
            if (originalRequest._retry) {
              await logout();
              return Promise.reject(error);
            }
            
            originalRequest._retry = true;
            
            try {
              // Attempt to refresh token
              const refreshResponse = await api.post('/api/refresh');
              // Cookies are automatically handled with withCredentials: true
              return api(originalRequest);
            } catch (refreshError) {
              // Clear auth state on refresh failure
              setIsAuthenticated(false);
              setUser(null);
              return Promise.reject(refreshError);
            }
          }
          
          // Reject non-authentication errors
          return Promise.reject(error);
        }
      );

      return () => {
        api.interceptors.request.eject(requestInterceptor);
        api.interceptors.response.eject(responseInterceptor);
      };
    }, []);

    const login = async (email: string, password: string) => {
        try {
            const response = await api.post('/api/auth/login', { email, password });
            setIsAuthenticated(true);
            const userData = response.data.user;
            setUser(userData);
            // Identify user in PostHog
            identifyUser(userData.id.toString(), {
                email: userData.email,
                name: userData.name,
                created_at: userData.created_at,
                auth_method: 'email'
            });
            window.location.href = '/dashboard';
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to login');
        }
    };

    const register = async (email: string, password: string) => {
        try {
            const response = await api.post('/api/auth/register', { email, password });
            setIsAuthenticated(true);
            const userData = response.data.user;
            setUser(userData);
            // Identify user in PostHog
            identifyUser(userData.id.toString(), {
                email: userData.email,
                name: userData.name,
                created_at: userData.created_at,
                auth_method: 'email',
                is_new_user: true
            });
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to register');
        }
    };

    const logout = async () => {
        try {
            await api.post('/api/logout');
            setIsAuthenticated(false);
            setUser(null);
            // Reset PostHog user
            resetUser();
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to logout');
        }
    };

    const signInWithGoogle = async () => {
        try {
            // Redirect to Google auth URL
            const response = await api.get('/api/auth/google/login');
            const { authUrl } = response.data;
            
            // Store current path for redirect back after auth
            sessionStorage.setItem('preAuthPath', window.location.pathname);
            
            // Redirect to Google auth URL
            window.location.href = authUrl;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to sign in with Google');
        }
    };

    const refreshUserData = async () => {
        try {
            const response = await api.get('/api/auth/me');
            setUser(response.data.user);
        } catch (error) {
            console.error('Failed to refresh user data:', error);
        }
    };

    const value = {
        isAuthenticated,
        isLoading,
        user,
        api,
        login,
        register,
        logout,
        signInWithGoogle,
        refreshUserData
    };

    // Don't render children until initial auth check is complete
    if (isLoading) {
        return <div>Loading...</div>;  // Or your loading component
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
